import React from "react";
import { Activity } from "lucide-react";

const ColumnHeatMap = ({ columnStatistics }) => {
  if (!columnStatistics || columnStatistics.length === 0) {
    return null;
  }

  // Group columns by dimension
  const dimensionGroups = columnStatistics.reduce((acc, col) => {
    // Add column to Completeness dimension if it has completeness metric
    if (col.completeness > 0) {
      if (!acc["Completeness"]) acc["Completeness"] = [];
      acc["Completeness"].push(col);
    }

    // Add column to Uniqueness dimension if it has uniqueness metric
    if (col.uniqueness > 0) {
      if (!acc["Uniqueness"]) acc["Uniqueness"] = [];
      acc["Uniqueness"].push(col);
    }

    // Add column to Validity dimension if it has validity metric
    if (col.validity > 0) {
      if (!acc["Validity"]) acc["Validity"] = [];
      acc["Validity"].push(col);
    }

    return acc;
  }, {});

  const dimensionColors = {
    Completeness: "#3b82f6",
    Uniqueness: "#f97316",
    Validity: "#10b981",
  };

  const getHeatMapColor = (failureRate) => {
    if (failureRate === 0) return "#86efac";
    else if (failureRate < 5) return "#bef264";
    else if (failureRate < 15) return "#fde047";
    else if (failureRate < 30) return "#fb923c";
    else if (failureRate < 50) return "#f87171";
    else return "#dc2626";
  };

  const colorCategories = [
    {
      color: "#86efac",
      title: "Excellent (0%)",
      desc: "No failures",
    },
    {
      color: "#bef264",
      title: "Good (<5%)",
      desc: "Minimal failures",
    },
    {
      color: "#fde047",
      title: "Average (5-15%)",
      desc: "Moderate failures",
    },
    {
      color: "#fb923c",
      title: "Warning (15-30%)",
      desc: "Needs attention",
    },
    {
      color: "#f87171",
      title: "High (30-50%)",
      desc: "Significant issues",
    },
    {
      color: "#dc2626",
      title: "Critical (>50%)",
      desc: "Immediate action",
    },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 overflow-hidden">
      <div className="p-6 border-b-2 border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          Dimension-wise Column Heatmap
        </h3>
      </div>

      {/* Gradient Legend */}
      <div className="flex items-center justify-center gap-4 py-4 px-6 bg-gray-50 border-b border-gray-200">
        <span className="text-sm font-semibold text-gray-600">Lowest</span>
        <div
          className="flex h-6 rounded-lg overflow-hidden shadow-sm"
          style={{ width: "300px" }}
        >
          <div className="flex-1 bg-gradient-to-r from-green-400 to-lime-300"></div>
          <div className="flex-1 bg-gradient-to-r from-lime-300 to-yellow-300"></div>
          <div className="flex-1 bg-gradient-to-r from-yellow-300 to-orange-400"></div>
          <div className="flex-1 bg-gradient-to-r from-orange-400 to-red-500"></div>
        </div>
        <span className="text-sm font-semibold text-gray-600">Highest</span>
      </div>

      {/* Heatmap Grid */}
      <div className="p-6">
        <div className="flex gap-6 justify-center">
          {Object.entries(dimensionGroups).map(([dimension, columns]) => {
            const color = dimensionColors[dimension] || "#64748b";

            return (
              <div key={dimension} className="flex items-start gap-4">
                {/* Dimension Header */}
                <div
                  className="w-40 py-3 px-4 rounded-l-xl font-bold text-white text-center text-sm flex items-center justify-center"
                  style={{ backgroundColor: color }}
                >
                  {dimension}
                </div>

                {/* Column Boxes */}
                <div className="flex flex-row gap-3 flex-wrap">
                  {columns.map((col, idx) => {
                    const successRate =
                      dimension === "Completeness"
                        ? col.completeness
                        : dimension === "Uniqueness"
                        ? col.uniqueness
                        : dimension === "Validity"
                        ? col.validity
                        : 0;
                    const failureRate = 100 - successRate;
                    const bgColor = getHeatMapColor(failureRate);

                    return (
                      <div
                        key={idx}
                        className="w-32 h-24 rounded-xl flex flex-col items-center justify-center shadow-md hover:shadow-xl transition-all duration-200 cursor-pointer border-2 border-white"
                        style={{ backgroundColor: bgColor }}
                        title={`${
                          col.column_name
                        } (${dimension}): ${failureRate.toFixed(
                          1
                        )}% failure rate`}
                      >
                        <div className="text-xs font-bold text-gray-800 mb-1 text-center px-2 truncate w-full">
                          {col.column_name}
                        </div>
                        <div className="text-2xl font-black text-gray-900">
                          {failureRate.toFixed(1)}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Color Categories Legend */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-4 p-6 bg-gray-50 border-t-2 border-gray-200">
        {colorCategories.map((category, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <div
              className="w-8 h-8 rounded-lg flex-shrink-0 shadow-sm"
              style={{ backgroundColor: category.color }}
            ></div>
            <div>
              <div className="text-xs font-bold text-gray-900">
                {category.title}
              </div>
              <div className="text-xs text-gray-600">{category.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ColumnHeatMap;